import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { FrameType, GalleryArtwork, GallerySceneSelection } from './types';

interface GallerySceneOptions {
  canvas: HTMLCanvasElement;
  artworks: GalleryArtwork[];
  onSelect: (selection: GallerySceneSelection) => void;
  onMove: (position: { x: number; z: number; yaw: number }) => void;
}

interface ArtworkObject {
  artwork: GalleryArtwork;
  group: THREE.Group;
  position: THREE.Vector3;
}

type Slot = {
  x: number;
  z: number;
  rotationY: number;
  kind: FrameType;
};

const visitorNames = ['Ethan', 'Olivia', 'Sophia', 'Lucas', 'Mia', 'Noah'];

export class GalleryScene {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(62, 1, 0.08, 120);
  private readonly renderer: THREE.WebGLRenderer;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly keys = new Set<string>();
  private readonly artworkObjects: ArtworkObject[] = [];
  private readonly clock = new THREE.Clock();
  private readonly onSelect: GallerySceneOptions['onSelect'];
  private readonly onMove: GallerySceneOptions['onMove'];
  private animationId = 0;
  private yaw = 0;
  private pitch = 0.14;
  private dragging = false;
  private lastPointer = { x: 0, y: 0 };

  constructor(options: GallerySceneOptions) {
    this.renderer = new THREE.WebGLRenderer({
      canvas: options.canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.scene.background = new THREE.Color('#b8c3c6');
    this.scene.fog = new THREE.Fog('#b8c3c6', 72, 150);
    this.onSelect = options.onSelect;
    this.onMove = options.onMove;

    this.camera.position.set(0, 2.55, 3.2);
    this.buildBaseLighting();
    this.loadSiteAsset();
    this.placeArtworks(options.artworks);
    this.buildProtagonist();
    this.buildVisitors();
    this.bindEvents(options.canvas);
    this.resize();
    this.selectNearestArtwork();
    this.animate();
  }

  dispose() {
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.resize);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    this.renderer.domElement.removeEventListener('pointerdown', this.handlePointerDown);
    this.renderer.domElement.removeEventListener('pointermove', this.handlePointerMove);
    this.renderer.domElement.removeEventListener('pointerup', this.handlePointerUp);
    this.renderer.domElement.removeEventListener('click', this.handleClick);
    this.renderer.dispose();
  }

  private buildBaseLighting() {
    this.scene.add(new THREE.HemisphereLight('#f3f7ff', '#776957', 1.15));

    const sun = new THREE.DirectionalLight('#e8f2ff', 1.8);
    sun.position.set(-8, 12, 9);
    sun.castShadow = true;
    sun.shadow.camera.left = -24;
    sun.shadow.camera.right = 24;
    sun.shadow.camera.top = 18;
    sun.shadow.camera.bottom = -18;
    sun.shadow.mapSize.set(2048, 2048);
    this.scene.add(sun);

    const atriumGlow = new THREE.PointLight('#fff0cf', 2.4, 18, 1.7);
    atriumGlow.position.set(0, 4.8, -2.2);
    this.scene.add(atriumGlow);

    [
      [-9.1, 5.5, -5.5],
      [9.1, 5.5, -5.5],
      [-7.2, 5.2, 5.4],
      [7.2, 5.2, 5.4],
      [0, 5.5, -13.5],
    ].forEach(([x, y, z]) => {
      const pool = new THREE.PointLight('#ffe6b8', 1.4, 8.5, 1.6);
      pool.position.set(x, y, z);
      this.scene.add(pool);
    });
  }

  private buildMuseum() {
    const floorMat = new THREE.MeshStandardMaterial({
      color: '#9da7a8',
      roughness: 0.38,
      metalness: 0.12,
    });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(42, 30), floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.scene.add(floor);

    const floorGrid = new THREE.GridHelper(42, 28, '#cfd6d6', '#9aa4a5');
    floorGrid.position.y = 0.012;
    const gridMaterial = floorGrid.material as THREE.Material;
    gridMaterial.transparent = true;
    gridMaterial.opacity = 0.28;
    this.scene.add(floorGrid);

    const routeMat = new THREE.MeshStandardMaterial({ color: '#c6cecd', roughness: 0.34, metalness: 0.08 });
    const entryRoute = new THREE.Mesh(new THREE.BoxGeometry(8.5, 0.035, 11.5), routeMat);
    entryRoute.position.set(-7.2, 0.035, 6.6);
    entryRoute.receiveShadow = true;
    this.scene.add(entryRoute);

    const ceiling = new THREE.Mesh(
      new THREE.PlaneGeometry(42, 30),
      new THREE.MeshStandardMaterial({ color: '#edf0ed', roughness: 0.72 }),
    );
    ceiling.position.y = 6.4;
    ceiling.rotation.x = Math.PI / 2;
    this.scene.add(ceiling);

    const wallMat = new THREE.MeshStandardMaterial({ color: '#cfc7b9', roughness: 0.62 });
    const wallSpecs = [
      [0, 3, -14, 42, 6, 0.3, 0],
      [0, 3, 14, 42, 6, 0.3, 0],
      [-21, 3, 0, 0.3, 6, 30, 0],
      [21, 3, 0, 0.3, 6, 30, 0],
      [-8.4, 2.7, -4, 0.35, 5.4, 11, 0],
      [8.4, 2.7, -4, 0.35, 5.4, 11, 0],
      [-7.6, 2.7, 5.4, 10, 5.4, 0.35, 0],
      [7.6, 2.7, 5.4, 10, 5.4, 0.35, 0],
      [-14.5, 2.55, 4.5, 0.32, 5.1, 8.2, 0],
      [14.5, 2.55, -1.5, 0.32, 5.1, 8.2, 0],
      [-2.8, 2.45, -9.2, 7.2, 4.9, 0.32, 0],
      [4.2, 2.45, 10.4, 8.5, 4.9, 0.32, 0],
      [-5.6, 2.35, 2.1, 5.8, 4.7, 0.34, 0],
      [4.9, 2.35, 1.2, 5.8, 4.7, 0.34, 0],
    ];
    wallSpecs.forEach(([x, y, z, w, h, d]) => {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), wallMat);
      wall.position.set(x, y, z);
      wall.castShadow = true;
      wall.receiveShadow = true;
      this.scene.add(wall);
      this.addBoxEdges(wall, '#6f746f', 0.22);
    });

    const glassMat = new THREE.MeshPhysicalMaterial({
      color: '#b8d4df',
      roughness: 0.08,
      metalness: 0,
      transmission: 0.34,
      transparent: true,
      opacity: 0.34,
      side: THREE.DoubleSide,
    });
    [-17, -10, -3, 4, 11, 18].forEach((x) => {
      const pane = new THREE.Mesh(new THREE.BoxGeometry(4.8, 3.5, 0.06), glassMat);
      pane.position.set(x, 3.25, -14.18);
      pane.castShadow = true;
      this.scene.add(pane);
    });

    const atrium = new THREE.Mesh(
      new THREE.CylinderGeometry(5.2, 5.7, 0.34, 96),
      new THREE.MeshStandardMaterial({ color: '#d8d1c3', roughness: 0.45, side: THREE.DoubleSide }),
    );
    atrium.position.set(0, 0.18, -2.2);
    atrium.castShadow = true;
    atrium.receiveShadow = true;
    this.scene.add(atrium);

    const basin = new THREE.Mesh(
      new THREE.CylinderGeometry(2.25, 2.45, 0.34, 80),
      new THREE.MeshStandardMaterial({ color: '#b9c4c3', roughness: 0.32, metalness: 0.08 }),
    );
    basin.position.set(0, 0.54, -2.2);
    basin.castShadow = true;
    basin.receiveShadow = true;
    this.scene.add(basin);
    this.addCylinderEdges(basin, '#606964', 0.34);

    const sculpture = new THREE.Group();
    const sculptureMat = new THREE.MeshStandardMaterial({ color: '#111111', roughness: 0.28, metalness: 0.2 });
    for (let i = 0; i < 3; i += 1) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.62 + i * 0.28, 0.035, 12, 80), sculptureMat);
      ring.position.set(0, 1.08 + i * 0.28, -2.2);
      ring.rotation.set(Math.PI / 2.4, i * 0.8, i * 0.45);
      ring.castShadow = true;
      sculpture.add(ring);
    }
    this.scene.add(sculpture);

    const skylight = new THREE.Mesh(
      new THREE.RingGeometry(3.2, 5.8, 96),
      new THREE.MeshStandardMaterial({
        color: '#c8d6df',
        roughness: 0.2,
        metalness: 0.05,
        side: THREE.DoubleSide,
      }),
    );
    skylight.position.set(0, 6.45, -2.2);
    skylight.rotation.x = -Math.PI / 2;
    this.scene.add(skylight);

    const balconyMat = new THREE.MeshStandardMaterial({ color: '#f2efe8', roughness: 0.48 });
    const balcony = new THREE.Mesh(new THREE.TorusGeometry(7.0, 0.16, 12, 128), balconyMat);
    balcony.position.set(0, 3.55, -2.2);
    balcony.rotation.x = Math.PI / 2;
    balcony.castShadow = true;
    this.scene.add(balcony);

    this.addColumns();
    this.addPlanting();

    this.scene.add(new THREE.HemisphereLight('#d8e7ff', '#8f826c', 1.35));
    const sun = new THREE.DirectionalLight('#e3f1ff', 1.7);
    sun.position.set(-6, 10, 7);
    sun.castShadow = true;
    sun.shadow.camera.left = -24;
    sun.shadow.camera.right = 24;
    sun.shadow.camera.top = 18;
    sun.shadow.camera.bottom = -18;
    sun.shadow.mapSize.set(2048, 2048);
    this.scene.add(sun);

    this.addLightRail(-8.4, -4, 0);
    this.addLightRail(8.4, -4, 0);
    this.addLightRail(0, 5.4, Math.PI / 2);
    this.addLightRail(0, -13.75, Math.PI / 2);
  }

  private loadSiteAsset() {
    const loader = new GLTFLoader();
    loader.load('/assets/artchais-gallery-site-v2.glb', (gltf) => {
      gltf.scene.name = 'artchais_gallery_site_asset';
      gltf.scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.castShadow = true;
          object.receiveShadow = true;
          if (object.material instanceof THREE.MeshStandardMaterial) {
            object.material.needsUpdate = true;
          }
        }
      });
      this.scene.add(gltf.scene);
    });
  }

  private addBoxEdges(mesh: THREE.Mesh, color: string, opacity: number) {
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(mesh.geometry),
      new THREE.LineBasicMaterial({ color, transparent: true, opacity }),
    );
    edges.position.copy(mesh.position);
    edges.rotation.copy(mesh.rotation);
    edges.scale.copy(mesh.scale);
    this.scene.add(edges);
  }

  private addCylinderEdges(mesh: THREE.Mesh, color: string, opacity: number) {
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(mesh.geometry, 18),
      new THREE.LineBasicMaterial({ color, transparent: true, opacity }),
    );
    edges.position.copy(mesh.position);
    edges.rotation.copy(mesh.rotation);
    edges.scale.copy(mesh.scale);
    this.scene.add(edges);
  }

  private addColumns() {
    const columnMat = new THREE.MeshStandardMaterial({ color: '#d4d0c6', roughness: 0.42 });
    const capMat = new THREE.MeshStandardMaterial({ color: '#f2eee6', roughness: 0.45 });
    [
      [-15.5, -10.5],
      [15.5, -10.5],
      [-15.5, 10.5],
      [15.5, 10.5],
      [-5.8, -4],
      [5.8, -4],
    ].forEach(([x, z]) => {
      const column = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.32, 5.8, 28), columnMat);
      column.position.set(x, 2.9, z);
      column.castShadow = true;
      column.receiveShadow = true;
      this.scene.add(column);
      const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.18, 28), capMat);
      cap.position.set(x, 5.85, z);
      cap.castShadow = true;
      this.scene.add(cap);
    });
  }

  private addPlanting() {
    const planterMat = new THREE.MeshStandardMaterial({ color: '#d8d1c2', roughness: 0.5 });
    const leafMat = new THREE.MeshStandardMaterial({ color: '#3f6d55', roughness: 0.8 });
    [
      [-6.2, 1.8],
      [6.6, 2.2],
      [-12.6, -8.6],
      [12.8, 8.6],
    ].forEach(([x, z]) => {
      const planter = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.72, 0.42, 32), planterMat);
      planter.position.set(x, 0.22, z);
      planter.castShadow = true;
      planter.receiveShadow = true;
      this.scene.add(planter);
      for (let i = 0; i < 9; i += 1) {
        const leaf = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.75, 10), leafMat);
        const angle = (i / 9) * Math.PI * 2;
        leaf.position.set(x + Math.cos(angle) * 0.24, 0.82, z + Math.sin(angle) * 0.24);
        leaf.rotation.set(0.7, 0, -angle);
        leaf.castShadow = true;
        this.scene.add(leaf);
      }
    });
  }

  private addLightRail(x: number, z: number, rotationY: number) {
    const rail = new THREE.Mesh(
      new THREE.BoxGeometry(9.5, 0.04, 0.05),
      new THREE.MeshStandardMaterial({ color: '#202020', roughness: 0.4 }),
    );
    rail.position.set(x, 5.72, z);
    rail.rotation.y = rotationY;
    this.scene.add(rail);

    for (let i = -2; i <= 2; i += 1) {
      const lamp = new THREE.Mesh(
        new THREE.CylinderGeometry(0.09, 0.13, 0.22, 20),
        new THREE.MeshStandardMaterial({ color: '#0e0e0e', roughness: 0.35 }),
      );
      lamp.position.set(x + Math.cos(rotationY) * i * 1.8, 5.52, z - Math.sin(rotationY) * i * 1.8);
      lamp.rotation.x = Math.PI / 2;
      this.scene.add(lamp);
    }
  }

  private makeSlots(): Slot[] {
    return [
      ...[-13.6, -8.2, -2.8, 2.8, 8.2, 13.6].map((x) => ({ x, z: -13.82, rotationY: 0, kind: 'monumental' as FrameType })),
      ...[-13.6, -8.2, -2.8, 2.8, 8.2, 13.6].map((x) => ({ x, z: 13.82, rotationY: Math.PI, kind: 'wall' as FrameType })),
      ...[-10.6, -5.3, 0, 5.3, 10.6].map((z) => ({ x: -20.82, z, rotationY: Math.PI / 2, kind: 'mural' as FrameType })),
      ...[-10.6, -5.3, 0, 5.3, 10.6].map((z) => ({ x: 20.82, z, rotationY: -Math.PI / 2, kind: 'mural' as FrameType })),
      ...[-10.0, -6.5, -3.0, 0.5, 4.0].map((z) => ({ x: -8.18, z, rotationY: -Math.PI / 2, kind: 'wall' as FrameType })),
      ...[-10.0, -6.5, -3.0, 0.5, 4.0].map((z) => ({ x: 8.18, z, rotationY: Math.PI / 2, kind: 'wall' as FrameType })),
      ...[-13.9, -10.8, -7.7, -4.6].map((z) => ({ x: -14.32, z, rotationY: -Math.PI / 2, kind: 'wall' as FrameType })),
      ...[-8.0, -4.8, -1.6, 1.6].map((z) => ({ x: 14.32, z, rotationY: Math.PI / 2, kind: 'wall' as FrameType })),
      ...[-5.2, -1.8, 1.8, 5.2].map((x) => ({ x, z: -9.02, rotationY: 0, kind: 'wall' as FrameType })),
      ...[-10.6, -6.8, -3.0, 3.0, 6.8, 10.6].map((x) => ({ x, z: 5.18, rotationY: Math.PI, kind: 'shelf' as FrameType })),
    ];
  }

  private placeArtworks(artworks: GalleryArtwork[]) {
    const textureLoader = new THREE.TextureLoader();
    textureLoader.setCrossOrigin('anonymous');
    const slots = this.makeSlots();
    const sorted = [...artworks].sort((a, b) => {
      const score = (item: GalleryArtwork) => (item.frameType === 'monumental' ? 0 : item.frameType === 'mural' ? 1 : 2);
      return score(a) - score(b);
    });

    sorted.slice(0, slots.length).forEach((artwork, index) => {
      const slot = slots[index % slots.length];
      const displayKind = artwork.frameType === 'shelf' ? 'shelf' : slot.kind === 'shelf' ? 'wall' : artwork.frameType;
      const group = this.createArtworkGroup(artwork, displayKind, textureLoader);
      group.position.set(slot.x, displayKind === 'shelf' ? 1.08 : 2.55, slot.z);
      group.rotation.y = slot.rotationY;
      this.scene.add(group);
      this.artworkObjects.push({ artwork, group, position: group.position.clone() });
      this.addSpotForArtwork(group.position, slot.rotationY, displayKind);
    });
  }

  private createArtworkGroup(
    artwork: GalleryArtwork,
    frameType: FrameType,
    textureLoader: THREE.TextureLoader,
  ): THREE.Group {
    const group = new THREE.Group();
    const maxWidth = frameType === 'monumental' ? 4.7 : frameType === 'mural' ? 3.45 : frameType === 'shelf' ? 1.35 : 2.35;
    const maxHeight = frameType === 'monumental' ? 3.2 : frameType === 'mural' ? 2.35 : frameType === 'shelf' ? 1.05 : 1.8;
    let width = maxWidth;
    let height = width / artwork.aspect;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * artwork.aspect;
    }
    const frameDepth = frameType === 'shelf' ? 0.09 : 0.12;
    const panelWidth = Math.max(width + 1.05, frameType === 'monumental' ? 5.35 : 3.25);
    const panelHeight = Math.max(height + 1.22, frameType === 'monumental' ? 4.25 : 3.15);
    const panel = new THREE.Mesh(
      new THREE.BoxGeometry(panelWidth, panelHeight, 0.16),
      new THREE.MeshStandardMaterial({ color: '#ddd6c8', roughness: 0.66 }),
    );
    panel.position.set(0, -0.1, -0.12);
    panel.castShadow = true;
    panel.receiveShadow = true;
    group.add(panel);

    const panelEdges = new THREE.LineSegments(
      new THREE.EdgesGeometry(panel.geometry),
      new THREE.LineBasicMaterial({ color: '#7c7c72', transparent: true, opacity: 0.32 }),
    );
    panelEdges.position.copy(panel.position);
    group.add(panelEdges);

    const frameColor = frameType === 'monumental' ? '#1f1710' : frameType === 'mural' ? '#6e5738' : '#151515';
    const back = new THREE.Mesh(
      new THREE.BoxGeometry(width + 0.18, height + 0.18, frameDepth),
      new THREE.MeshStandardMaterial({ color: frameColor, roughness: 0.46, metalness: 0.05 }),
    );
    back.castShadow = true;
    back.receiveShadow = true;
    group.add(back);

    const mat = new THREE.Mesh(
      new THREE.BoxGeometry(width + 0.04, height + 0.04, frameDepth + 0.01),
      new THREE.MeshStandardMaterial({ color: '#f4efe5', roughness: 0.78 }),
    );
    mat.position.z = 0.014;
    group.add(mat);

    const texture = textureLoader.load(artwork.imageUrl, (loadedTexture) => {
      loadedTexture.needsUpdate = true;
    });
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide }),
    );
    plane.position.z = 0.08;
    plane.castShadow = true;
    group.add(plane);
    group.userData.artworkId = artwork.id;

    if (frameType === 'shelf') {
      const shelf = new THREE.Mesh(
        new THREE.BoxGeometry(width + 0.5, 0.14, 0.58),
        new THREE.MeshStandardMaterial({ color: '#d9d1c2', roughness: 0.52 }),
      );
      shelf.position.set(0, -height / 2 - 0.23, -0.05);
      shelf.castShadow = true;
      shelf.receiveShadow = true;
      group.add(shelf);
    }

    const label = this.makeLabel(artwork.title, artwork.artistName);
    label.position.set(0, -height / 2 - (frameType === 'shelf' ? 0.55 : 0.36), 0.09);
    group.add(label);
    return group;
  }

  private makeLabel(title: string, artistName: string) {
    const canvas = document.createElement('canvas');
    canvas.width = 768;
    canvas.height = 160;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = 'rgba(250,247,239,0.96)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#161616';
    ctx.font = '700 44px Inter, Arial';
    ctx.fillText(title.slice(0, 28), 42, 62);
    ctx.fillStyle = '#6c665e';
    ctx.font = '500 30px Inter, Arial';
    ctx.fillText(artistName.slice(0, 34), 42, 112);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return new THREE.Mesh(
      new THREE.PlaneGeometry(1.9, 0.4),
      new THREE.MeshBasicMaterial({ map: texture, transparent: true }),
    );
  }

  private addSpotForArtwork(position: THREE.Vector3, rotationY: number, frameType: FrameType) {
    const offset = new THREE.Vector3(Math.sin(rotationY) * 1.4, 0, Math.cos(rotationY) * 1.4);
    const spot = new THREE.SpotLight('#ffe3b4', frameType === 'monumental' ? 5 : 3.6, 9, frameType === 'shelf' ? 0.38 : 0.28, 0.58, 1.1);
    spot.position.set(position.x + offset.x, 5.52, position.z + offset.z);
    spot.target.position.set(position.x, position.y, position.z);
    spot.castShadow = true;
    spot.shadow.mapSize.set(1024, 1024);
    this.scene.add(spot, spot.target);
  }

  private buildVisitors() {
    const visitorPositions = [
      [-7.3, 11.4],
      [-3.6, 6.5],
      [3.1, 3.4],
      [-13, -2],
      [13, -7.5],
      [-4.2, -7.5],
      [6.8, -10.4],
    ];
    visitorPositions.forEach(([x, z], index) => {
      const visitor = new THREE.Group();
      const body = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.2, 0.78, 8, 16),
        new THREE.MeshStandardMaterial({ color: index % 2 ? '#2f3336' : '#4b4540', roughness: 0.7 }),
      );
      body.position.y = 0.95;
      body.castShadow = true;
      const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.17, 20, 20),
        new THREE.MeshStandardMaterial({ color: '#c9a58e', roughness: 0.6 }),
      );
      head.position.y = 1.55;
      head.castShadow = true;
      visitor.add(body, head);
      visitor.position.set(x, 0, z);
      visitor.rotation.y = Math.atan2(-x, -z);
      visitor.userData.name = visitorNames[index] || 'Guest';
      this.scene.add(visitor);
    });
  }

  private buildProtagonist() {
    const human = new THREE.Group();
    const cloth = new THREE.MeshStandardMaterial({ color: '#111418', roughness: 0.72 });
    const denim = new THREE.MeshStandardMaterial({ color: '#1f3342', roughness: 0.68 });
    const skin = new THREE.MeshStandardMaterial({ color: '#c8a28d', roughness: 0.6 });
    const hair = new THREE.MeshStandardMaterial({ color: '#17130f', roughness: 0.65 });

    const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.25, 0.86, 10, 18), cloth);
    torso.position.y = 1.18;
    torso.castShadow = true;
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.18, 24, 24), skin);
    head.position.y = 1.78;
    head.castShadow = true;
    const hairCap = new THREE.Mesh(new THREE.SphereGeometry(0.185, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2), hair);
    hairCap.position.y = 1.84;
    hairCap.castShadow = true;

    const leftLeg = new THREE.Mesh(new THREE.CapsuleGeometry(0.085, 0.62, 8, 12), denim);
    leftLeg.position.set(-0.11, 0.48, 0);
    leftLeg.castShadow = true;
    const rightLeg = leftLeg.clone();
    rightLeg.position.x = 0.11;
    const leftArm = new THREE.Mesh(new THREE.CapsuleGeometry(0.055, 0.58, 8, 12), skin);
    leftArm.position.set(-0.34, 1.15, 0);
    leftArm.rotation.z = 0.12;
    leftArm.castShadow = true;
    const rightArm = leftArm.clone();
    rightArm.position.x = 0.34;
    rightArm.rotation.z = -0.12;

    human.add(torso, head, hairCap, leftLeg, rightLeg, leftArm, rightArm);
    human.position.set(0, 0.02, 6.4);
    human.rotation.y = 0;
    human.scale.setScalar(1.08);
    this.scene.add(human);
  }

  private bindEvents(canvas: HTMLCanvasElement) {
    window.addEventListener('resize', this.resize);
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    canvas.addEventListener('pointerdown', this.handlePointerDown);
    canvas.addEventListener('pointermove', this.handlePointerMove);
    canvas.addEventListener('pointerup', this.handlePointerUp);
    canvas.addEventListener('click', this.handleClick);
  }

  private resize = () => {
    const parent = this.renderer.domElement.parentElement;
    const width = parent?.clientWidth || window.innerWidth;
    const height = parent?.clientHeight || window.innerHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    this.keys.add(event.key.toLowerCase());
  };

  private handleKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.key.toLowerCase());
  };

  private handlePointerDown = (event: PointerEvent) => {
    this.dragging = true;
    this.lastPointer = { x: event.clientX, y: event.clientY };
    this.renderer.domElement.setPointerCapture(event.pointerId);
  };

  private handlePointerMove = (event: PointerEvent) => {
    if (!this.dragging) return;
    const dx = event.clientX - this.lastPointer.x;
    const dy = event.clientY - this.lastPointer.y;
    this.yaw -= dx * 0.0032;
    this.pitch = THREE.MathUtils.clamp(this.pitch - dy * 0.0025, -0.78, 0.68);
    this.lastPointer = { x: event.clientX, y: event.clientY };
  };

  private handlePointerUp = (event: PointerEvent) => {
    this.dragging = false;
    if (this.renderer.domElement.hasPointerCapture(event.pointerId)) {
      this.renderer.domElement.releasePointerCapture(event.pointerId);
    }
  };

  private handleClick = (event: MouseEvent) => {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.scene.children, true);
    const hit = hits.find((item) => this.findArtworkObject(item.object));
    if (hit) {
      const artworkObject = this.findArtworkObject(hit.object);
      if (artworkObject) this.emitSelection(artworkObject);
    }
  };

  private findArtworkObject(object: THREE.Object3D): ArtworkObject | undefined {
    let current: THREE.Object3D | null = object;
    while (current) {
      const found = this.artworkObjects.find((item) => item.group === current);
      if (found) return found;
      current = current.parent;
    }
    return undefined;
  }

  private animate = () => {
    const delta = Math.min(this.clock.getDelta(), 0.04);
    this.updateMovement(delta);
    this.animationId = requestAnimationFrame(this.animate);
    this.renderer.render(this.scene, this.camera);
  };

  private updateMovement(delta: number) {
    if (this.keys.has('arrowleft')) this.yaw += delta * 1.65;
    if (this.keys.has('arrowright')) this.yaw -= delta * 1.65;
    if (this.keys.has('arrowup')) this.pitch = THREE.MathUtils.clamp(this.pitch + delta * 1.05, -0.78, 0.68);
    if (this.keys.has('arrowdown')) this.pitch = THREE.MathUtils.clamp(this.pitch - delta * 1.05, -0.78, 0.68);

    const speed = this.keys.has('shift') ? 5.8 : 3.1;
    const forward = new THREE.Vector3(Math.sin(this.yaw), 0, Math.cos(this.yaw) * -1);
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, Math.sin(this.yaw));
    const move = new THREE.Vector3();
    if (this.keys.has('w')) move.add(forward);
    if (this.keys.has('s')) move.sub(forward);
    if (this.keys.has('d')) move.add(right);
    if (this.keys.has('a')) move.sub(right);
    if (move.lengthSq() > 0) {
      move.normalize().multiplyScalar(speed * delta);
      this.camera.position.add(move);
      this.camera.position.x = THREE.MathUtils.clamp(this.camera.position.x, -18.5, 18.5);
      this.camera.position.z = THREE.MathUtils.clamp(this.camera.position.z, -12.4, 12.4);
    }

    const euler = new THREE.Euler(this.pitch, this.yaw, 0, 'YXZ');
    this.camera.quaternion.setFromEuler(euler);
    this.onMove({ x: this.camera.position.x, z: this.camera.position.z, yaw: this.yaw });
  }

  private selectNearestArtwork() {
    const nearest = this.artworkObjects
      .map((item) => ({
        item,
        distance: item.position.distanceTo(this.camera.position),
      }))
      .sort((a, b) => a.distance - b.distance)[0]?.item;
    if (nearest) this.emitSelection(nearest);
  }

  private emitSelection(item: ArtworkObject) {
    this.onSelect({
      id: item.artwork.id,
      title: item.artwork.title,
      artistName: item.artwork.artistName,
      description: item.artwork.description,
      imageUrl: item.artwork.imageUrl,
      sourceUrl: item.artwork.sourceUrl,
      frameType: item.artwork.frameType,
      price: item.artwork.price,
      currency: item.artwork.currency,
      position: { x: item.position.x, z: item.position.z },
    });
  }
}
